import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * A funeral home's arrangements. Mirrors api/Funely/Arrangement.cs. Personal data about a
 * death, so every call here is signed in and members-only; there is no public variant.
 */
export const ARRANGEMENT_STATUS = {
  ENQUIRY: 1,
  ARRANGING: 2,
  CONFIRMED: 3,
  COMPLETED: 4,
  CANCELLED: 5,
} as const;

export type ArrangementStatus = (typeof ARRANGEMENT_STATUS)[keyof typeof ARRANGEMENT_STATUS];

export const ARRANGEMENT_STATUS_LABEL: Record<ArrangementStatus, string> = {
  1: "Enquiry",
  2: "Arranging",
  3: "Confirmed",
  4: "Completed",
  5: "Cancelled",
};

export type ArrangementStep = {
  stepId: number;
  arrangementId: number;
  /** ISO 8601 in UTC. */
  startsAt: string;
  title: string;
  place: string;
  isDone: boolean;
};

export type Arrangement = {
  arrangementId: number;
  orgId: number;
  familyContactId: number | null;
  deceasedFullName: string;
  /** yyyy-MM-dd or empty. */
  dateOfBirth: string;
  dateOfDeath: string;
  placeOfDeath: string;
  status: ArrangementStatus;
  funeralDate: string;
  notes: string;
  steps: ArrangementStep[];
};

export const MAX_NAME_LENGTH = 200;
export const MAX_NOTES_LENGTH = 4000;

export const newArrangement = (orgId: number): Arrangement => ({
  arrangementId: 0,
  orgId,
  familyContactId: null,
  deceasedFullName: "",
  dateOfBirth: "",
  dateOfDeath: "",
  placeOfDeath: "",
  status: ARRANGEMENT_STATUS.ENQUIRY,
  funeralDate: "",
  notes: "",
  steps: [],
});

/** Why an arrangement cannot be saved, or null. Same checks as ArrangementRules. */
export const validateArrangement = (a: Arrangement, today = new Date()): string | null => {
  if (!a.orgId || a.orgId <= 0) return "Choose an active provider first.";
  if (!a.deceasedFullName.trim()) return "Give the name of the person who has died.";
  if (a.deceasedFullName.trim().length > MAX_NAME_LENGTH) return `The name is at most ${MAX_NAME_LENGTH} characters.`;
  if (a.notes.length > MAX_NOTES_LENGTH) return `Notes are at most ${MAX_NOTES_LENGTH} characters.`;
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  if (a.dateOfDeath && a.dateOfDeath > tomorrow) return "The date of death is in the future.";
  if (a.dateOfBirth && a.dateOfDeath && a.dateOfDeath < a.dateOfBirth) return "The date of death is before the date of birth.";
  if (a.dateOfDeath && a.funeralDate && a.funeralDate < a.dateOfDeath) return "The funeral date is before the date of death.";
  return null;
};

type Envelope<T> = { data?: T | null };

const unwrap = <T>(result: VgApiResult<Envelope<T>>): VgApiResult<T> =>
  result.error ? { data: null, error: result.error } : { data: (result.data?.data as T) ?? null, error: null };

export const readArrangements = async (accessToken: string, orgId: number): Promise<VgApiResult<Arrangement[]>> => {
  const result = unwrap(await vgApiRequest<Envelope<Arrangement[]>>(accessToken, "Read_Arrangements", { orgId }));
  return result.error ? result : { data: result.data ?? [], error: null };
};

export const readArrangement = async (
  accessToken: string,
  orgId: number,
  arrangementId: number
): Promise<VgApiResult<Arrangement>> =>
  unwrap(await vgApiRequest<Envelope<Arrangement>>(accessToken, "Read_Arrangement", { orgId, arrangementId }));

/** Creates a new arrangement (arrangementId 0) or saves an existing one. */
export const saveArrangement = async (accessToken: string, a: Arrangement): Promise<VgApiResult<Arrangement>> => {
  const invalid = validateArrangement(a);
  if (invalid) return { data: null, error: { message: invalid } };
  const { steps, ...body } = a;
  return unwrap(
    await vgApiRequest<Envelope<Arrangement>>(
      accessToken,
      a.arrangementId > 0 ? "Update_Arrangement" : "Create_Arrangement",
      { ...body, deceasedFullName: a.deceasedFullName.trim() }
    )
  );
};

/** Adds a step (stepId 0) or saves one; the response is the whole arrangement. */
export const saveStep = async (
  accessToken: string,
  orgId: number,
  arrangementId: number,
  step: ArrangementStep
): Promise<VgApiResult<Arrangement>> => {
  if (!step.title.trim()) return { data: null, error: { message: "Say what happens, for example Service at the chapel." } };
  if (!step.startsAt || Number.isNaN(new Date(step.startsAt).getTime()))
    return { data: null, error: { message: "Give the time it starts." } };
  return unwrap(
    await vgApiRequest<Envelope<Arrangement>>(
      accessToken,
      step.stepId > 0 ? "Update_Arrangement_Step" : "Create_Arrangement_Step",
      { orgId, arrangementId, step: { ...step, startsAt: new Date(step.startsAt).toISOString(), title: step.title.trim() } }
    )
  );
};

export const deleteStep = async (
  accessToken: string,
  orgId: number,
  arrangementId: number,
  step: ArrangementStep
): Promise<VgApiResult<Arrangement>> =>
  unwrap(
    await vgApiRequest<Envelope<Arrangement>>(accessToken, "Delete_Arrangement_Step", { orgId, arrangementId, step })
  );
